import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import CollapseHeader from "../../../core/common/collapse-header/collapse-header";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import { DatePicker, TimePicker } from "antd";
import dayjs from "dayjs";
import useApi from "../../../core/customhook/useApi";
import Swal from "sweetalert2";

const MyAttendance = () => {
  const [selectedDate, setSelectedDate] = useState<string>("");
  const [reguDate, setReguDate] = useState<dayjs.Dayjs | null>(dayjs());
  const [calendarEvents, setCalendarEvents] = useState<any>([]);
  const [hoveredEvent, setHoveredEvent] = useState<any>(null);
  const [months, setMonths] = useState("");
  const [year, setYear] = useState("");
  const [inTime, setInTime] = useState<dayjs.Dayjs | null>(
    dayjs("09:00:00", "HH:mm:ss"),
  );
  const [outTime, setOutTime] = useState<dayjs.Dayjs | null>(
    dayjs("19:00:00", "HH:mm:ss"),
  );
  const [reason, setReason] = useState("");

  useEffect(() => {
    const today = new Date();
    const todayFormatted = today.toISOString().split("T")[0];
    setSelectedDate(todayFormatted);
  }, []);

  const handleDateClick = (info: any) => {
    const curruntDate = dayjs().format("YYYY-MM-DD");
    if (info.dateStr >= curruntDate) {
      Swal.fire({
        title: "Not allowed",
        text: "You can't select today's or future dates.",
        icon: "info",
        confirmButtonText: "OK",
      });
      return;
    }
    const selectedDayjs = dayjs(info.date);
    setReguDate(selectedDayjs);
  };

  const {
    data: attendanceData,
    loading: attendanceLoading,
    error: attendanceErrro,
    callApi,
  } = useApi(`/api/get-attendance-details?year=${year}&month=${months}`, {
    method: "GET",
    autoCall: false,
  });

  useEffect(() => {
    if (!year || !months) return;

    callApi();
  }, [year, months]);

  useEffect(() => {
    if (!attendanceData?.success) return;

    // 1️⃣ Map leave_type_id to short code
    const leaveTypeShortMap: Record<number, string> = {
      1: "EL", // Earned Leave
      2: "SL", // Sick Leave
      3: "RH", // Restricted Holiday
      4: "ML", // Maternity Leave
      5: "LWP", // Leave Without Pay
      7: "BL", // Bereavement Leave
      8: "WL", // Wedding Leave
      9: "HL", // Happiness Leave
    };

    // 2️⃣ Map leave dates with description
    const leaveByDate: Record<
      string,
      { code: string; description?: string; isHalfDay: boolean }
    > = {};
    attendanceData.approvedLeaves?.forEach((leave: any) => {
      const from = dayjs(leave.from_date);
      const to = leave.to_date ? dayjs(leave.to_date) : from;

      for (let d = from; d.isSameOrBefore(to); d = d.add(1, "day")) {
        leaveByDate[d.format("YYYY-MM-DD")] = {
          code: leaveTypeShortMap[leave.leave_type_id] || "LEAVE",
          isHalfDay: leave.days === 0.5,
          description: leave.description,
        };
      }
    });

    // 3️⃣ Map OD dates with description
    const odByDate: Record<
      string,
      { code: string; description?: string; isHalfDay: boolean }
    > = {};
    attendanceData.approvedODs?.forEach((od: any) => {
      const from = dayjs(od.from_date);
      const to = od.to_date ? dayjs(od.to_date) : from;

      for (let d = from; d.isSameOrBefore(to); d = d.add(1, "day")) {
        odByDate[d.format("YYYY-MM-DD")] = {
          code: "OD",
          isHalfDay: od.days === 0.5,
          description: od.description,
        };
      }
    });

    // 4️⃣ Map attendance events with priority: Leave > OD > Attendance
    const attendanceEvents = attendanceData.data.map((record: any) => {
      const date = record.attendance_date;

      let displayStatus = record.status;
      let title = record.status;

      // Leave has priority
      if (leaveByDate[date]) {
        displayStatus = leaveByDate[date].code;
        title = leaveByDate[date].isHalfDay
          ? `${displayStatus} (${leaveByDate[date].description})`
          : displayStatus;
      }
      // OD second priority
      else if (odByDate[date]) {
        displayStatus = odByDate[date].code;
        title = odByDate[date].isHalfDay
          ? `${displayStatus} (${odByDate[date].description})`
          : displayStatus;
      }

      // 5️⃣ Determine color for the dot
      let color =
        displayStatus === "P"
          ? "#28a745" // Present
          : displayStatus === "A"
            ? "#dc3545" // Absent
            : displayStatus === "HP"
              ? "#ffc107" // Half Pay
              : displayStatus === "WO"
                ? "#007bff" // Work Off
                : displayStatus === "SH"
                  ? "#fd7e14" // Special Holiday
                  : displayStatus === "EL"
                    ? "#17a2b8" // Earned Leave
                    : displayStatus === "SL"
                      ? "#6f42c1" // Sick Leave
                      : displayStatus === "RH"
                        ? "#20c997" // Restricted Holiday
                        : displayStatus === "ML"
                          ? "#fd7e14" // Maternity Leave
                          : displayStatus === "LWP"
                            ? "#dc3545" // Leave Without Pay (red)
                            : displayStatus === "BL"
                              ? "#6610f2" // Bereavement Leave
                              : displayStatus === "WL"
                                ? "#ffc107" // Wedding Leave
                                : displayStatus === "HL"
                                  ? "#007bff" // Happiness Leave
                                  : displayStatus === "OD"
                                    ? "#6610f2" // On Duty
                                    : "#6c757d"; // Default gray

      let inTime = record.in_time_machine;
      let outTime = record.out_time_machine;

      if (
        record.regularisation_status === "APPROVED" &&
        record.in_time_machine === "00:00:00" &&
        record.out_time_machine === "00:00:00"
      ) {
        inTime = record.self_regn_in_time || "-";
        outTime = record.self_regn_out_time || "-";
      }

      return {
        title,
        date,
        color,
        extendedProps: {
          attendance_log_id: record.attendance_log_id,
          status: record.status,
          leaveCode: leaveByDate[date]?.code || null,
          isHalfDay:
            leaveByDate[date]?.isHalfDay || odByDate[date]?.isHalfDay || false,
          leaveDescription: leaveByDate[date]?.description || null,
          odCode: odByDate[date]?.code || null,
          odDescription: odByDate[date]?.description || null,
          in_time: inTime,
          out_time: outTime,
        },
      };
    });

    // 6️⃣ Map holidays
    const holidayEvents =
      attendanceData.holidays?.map((holiday: any) => {
        const isRH = holiday.holiday_category === "RH";
        return {
          title: holiday.holiday,
          date: holiday.holiday_date,
          backgroundColor: "transparent",
          extendedProps: {
            isHoliday: true,
            category: holiday.holiday_category,
            textColor: isRH ? "#007bff" : "#dc3545",
          },
        };
      }) || [];

    // 7️⃣ Combine all events
    const allEvents = [...attendanceEvents, ...holidayEvents];
    setCalendarEvents(allEvents);
  }, [attendanceData]);

  const {
    data: addReguData,
    loading: addReguLoading,
    error: addReguErrro,
    callApi: AddReguRecord,
  } = useApi("/api/regularization-attendace", {
    method: "POST",
    autoCall: false,
  });

  const postData = async (e: any) => {
    e.preventDefault();
    try {
      if (!reguDate || !inTime || !outTime) {
        console.error("Date or Time values are missing");
        return;
      }
      const payload = {
        attendance_regn_date: reguDate.format("YYYY-MM-DD"),
        self_regn_in_date: reguDate.format("YYYY-MM-DD"),
        self_regn_in_time: inTime.format("HH:mm:ss"),
        self_regn_out_date: reguDate.format("YYYY-MM-DD"),
        self_regn_out_time: outTime.format("HH:mm:ss"),
        reason: reason,
      };

      const response = await AddReguRecord(payload);

      if (response?.success) {
        Swal.fire({
          icon: "success",
          title: "Submitted!",
          text: response?.message || "Regularization submitted successfully.",
          timer: 2000,
          showConfirmButton: true,
        });
        document.getElementById("closed_modal")?.click();
        setReason("");
        setOutTime(dayjs("19:00:00", "HH:mm:ss"));
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: response?.message || "Something went wrong!",
        });
      }
    } catch (error: any) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data.message || "Something went wrong!",
      });
    }
  };

  const handleDoubleClick = (date: any) => {
    setReguDate(dayjs(date));
    setInTime(null);
    setOutTime(null);
    setReason("");

    document.getElementById("openAttendanceModalBtn")?.click();
  };

  console.log(calendarEvents);

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          {/* Breadcrumb */}
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-2">
            <div className="my-auto mb-2">
              <h2 className="mb-2">My Attendance</h2>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={all_routes.employeeDashboard}>
                      <i className="ti ti-smart-home" />
                    </Link>
                  </li>
                  <li className="breadcrumb-item">My Attendance</li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Attendance
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              <div className="head-icons ms-2">
                <CollapseHeader />
              </div>
            </div>
          </div>
          {/* /Breadcrumb */}

          <div className="row">
            <div className="col-md-12 col-xxl-12 col-xl-12  theiaStickySidebar">
              <div className="stickybar">
                <div className="card border-0">
                  <div className="card-body">
                    <FullCalendar
                      plugins={[
                        dayGridPlugin,
                        timeGridPlugin,
                        interactionPlugin,
                      ]}
                      initialView="dayGridMonth"
                      headerToolbar={{
                        start: "",
                        center: "title",
                        end: "prev,next",
                      }}
                      height={500}
                      events={calendarEvents}
                      datesSet={(info) => {
                        const month = dayjs(info.view.currentStart).format(
                          "MM",
                        );
                        const year = dayjs(info.view.currentStart).format(
                          "YYYY",
                        );
                        setMonths(month);
                        setYear(year);
                      }}
                      dayCellDidMount={(arg) => {
                        arg.el.style.cursor = "pointer";

                        let lastTapTime = 0;

                        arg.el.addEventListener("click", () => {
                          const now = Date.now();

                          if (now - lastTapTime < 250) {
                            const selectedDate = dayjs(arg.date).format(
                              "YYYY-MM-DD",
                            );
                            handleDoubleClick(selectedDate);
                          }

                          lastTapTime = now;
                        });
                      }}
                      dayCellContent={({ date }) => {
                        const formattedDate = dayjs(date).format("YYYY-MM-DD");
                        const matchedEvent = calendarEvents.find(
                          (e: any) => e.date === formattedDate,
                        );

                        let dots: { color: string; title: string }[] = [];

                        if (matchedEvent) {
                          const { title, extendedProps } = matchedEvent;

                          // Holiday dot
                          if (extendedProps?.isHoliday) {
                            const color =
                              extendedProps.category === "RH"
                                ? "#007bff"
                                : "#dc3545";
                            dots.push({ color, title });
                          } else {
                            // Attendance / status color mapping
                            const colorMap: Record<string, string> = {
                              P: "#28a745",
                              A: "#dc3545",
                              HP: "#ffc107",
                              WO: "#007bff",
                              SH: "#fd7e14",
                              EL: "#17a2b8",
                              SL: "#6f42c1",
                              RH: "#20c997",
                              OD: "#6610f2",
                            };

                            // Split title in case it contains Leave/OD combined (like "SH / SL")
                            const titles = title.split(" / ");

                            titles.forEach((t: any) => {
                              const color = colorMap[t] || "#6c757d";
                              dots.push({ color, title: t });
                            });
                          }
                        }

                        return (
                          <div
                            style={{
                              position: "relative",
                              textAlign: "center",
                            }}
                          >
                            <div>{date.getDate()}</div>
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "center",
                                marginTop: "5px",
                                gap: "4px",
                              }}
                            >
                              {dots.map((dot, idx) => (
                                <span
                                  key={idx}
                                  title={dot.title}
                                  style={{
                                    width: 10,
                                    height: 10,
                                    backgroundColor: dot.color,
                                    borderRadius: "50%",
                                    display: "inline-block",
                                  }}
                                />
                              ))}
                            </div>
                          </div>
                        );
                      }}
                      eventContent={(eventInfo) => {
                        const textColor =
                          eventInfo.event.extendedProps.textColor || "white";

                        return (
                          <div
                            style={{
                              color: textColor,
                              fontWeight: "bold",
                              fontSize: "11px",
                              textAlign: "center",
                              userSelect: "none",
                              whiteSpace: "nowrap",
                            }}
                            title={eventInfo.event.title}
                          >
                            {eventInfo.event.title}
                          </div>
                        );
                      }}
                      eventMouseEnter={(info) => {
                        const event = info.event.extendedProps;
                        setHoveredEvent({
                          date: info.event.startStr,
                          status: event.status,
                          in_time: event.in_time,
                          out_time: event.out_time,
                          x: info.jsEvent.pageX,
                          y: info.jsEvent.pageY,
                        });
                      }}
                      eventMouseLeave={() => setHoveredEvent(null)}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="row mt-2">
            <div className="col-12">
              <div className="card p-4 shadow-sm">
                <h4 className="mb-3">Status Legend</h4>
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "35px",
                  }}
                >
                  {/* Present */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                    }}
                  >
                    <span
                      style={{
                        width: "16px",
                        height: "16px",
                        backgroundColor: "#28a745", // green
                        borderRadius: "4px",
                        display: "inline-block",
                      }}
                    ></span>
                    <span>Present</span>
                  </div>

                  {/* Absent */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                    }}
                  >
                    <span
                      style={{
                        width: "16px",
                        height: "16px",
                        backgroundColor: "#dc3545", // red
                        borderRadius: "4px",
                        display: "inline-block",
                      }}
                    ></span>
                    <span>Absent</span>
                  </div>

                  {/* Half Day */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                    }}
                  >
                    <span
                      style={{
                        width: "16px",
                        height: "16px",
                        backgroundColor: "#ffc107", // orange
                        borderRadius: "4px",
                        display: "inline-block",
                      }}
                    ></span>
                    <span>Half Day</span>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                    }}
                  >
                    <span
                      style={{
                        width: "16px",
                        height: "16px",
                        backgroundColor: "#fd7e14", // yellow
                        borderRadius: "4px",
                        display: "inline-block",
                      }}
                    ></span>
                    <span>Short Hour</span>
                  </div>

                  {/* Weekend Off */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                    }}
                  >
                    <span
                      style={{
                        width: "16px",
                        height: "16px",
                        backgroundColor: "#007bff", // yellow (WO)
                        borderRadius: "4px",
                        display: "inline-block",
                      }}
                    ></span>
                    <span>Weekend Off</span>
                  </div>

                  {/* RH Holiday */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                    }}
                  >
                    <span
                      style={{
                        width: "16px",
                        height: "16px",
                        backgroundColor: "transparent",
                        border: "2px solid #007bff", // blue border for RH
                        borderRadius: "4px",
                        display: "inline-block",
                      }}
                    ></span>
                    <span>Restricted Holiday (RH)</span>
                  </div>

                  {/* MH Holiday */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                    }}
                  >
                    <span
                      style={{
                        width: "16px",
                        height: "16px",
                        backgroundColor: "#dc3545", // red
                        borderRadius: "4px",
                        display: "inline-block",
                      }}
                    ></span>
                    <span>Mandatory Holiday (MH)</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <button
          id="openAttendanceModalBtn"
          data-bs-toggle="modal"
          data-bs-target="#attendanceModal"
          style={{ display: "none" }}
        >
          Hidden Modal Button
        </button>

        {/* Footer */}
        <div className="footer d-sm-flex align-items-center justify-content-center border-top bg-white p-3">
          <p className="mb-0">© {new Date().getFullYear()} WeConnect. All rights reserved.</p>
        </div>
      </div>

      {hoveredEvent && (
        <div
          style={{
            position: "absolute",
            top: hoveredEvent.y + 10,
            left: hoveredEvent.x + 10,
            background: "white",
            border: "1px solid #ccc",
            borderRadius: "6px",
            padding: "8px 12px",
            boxShadow: "0px 2px 10px rgba(0,0,0,0.1)",
            zIndex: 9999,
            fontSize: "13px",
            minWidth: "180px",
          }}
        >
          <strong>Date:</strong> {hoveredEvent.date} <br />
          <strong>Status:</strong> {hoveredEvent.status} <br />
          <strong>Punch In:</strong> {hoveredEvent.in_time || "—"} <br />
          <strong>Punch Out:</strong> {hoveredEvent.out_time || "—"}
        </div>
      )}

      <div
        className="modal fade"
        id="attendanceModal"
        tabIndex={-1}
        aria-labelledby="exampleModalLgLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-lg modal-dialog-centered">
          <div className="modal-content">
            <form onSubmit={postData}>
              <div className="modal-header">
                <h4 className="modal-title" id="exampleModalLgLabel">
                  Attendance Regularization
                </h4>
                <button
                  type="button"
                  className="btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                  id="closed_modal"
                />
              </div>
              <div className="modal-body">
                <div className="row mt-3 mb-3">
                  {/* In Time */}
                  <div className="col-md-12 d-flex align-items-center gap-3">
                    <label className="form-label w-25">In Time</label>
                    <div className="input-icon-end position-relative flex-grow-1">
                      <DatePicker
                        className="form-control datetimepicker"
                        format="YYYY-MM-DD"
                        value={reguDate}
                        onChange={(date) => setReguDate(date)}
                        placeholder="YYYY-MM-DD"
                        disabled
                        // disabledDate={(current) => {
                        //   return current && current >= dayjs().startOf("day");
                        // }}
                      />
                      <span className="input-icon-addon">
                        <i className="ti ti-calendar text-gray-7" />
                      </span>
                    </div>
                    <TimePicker
                      className="form-control w-25"
                      value={inTime}
                      onChange={(value) => setInTime(value)}
                      format="HH:mm:ss"
                      allowClear={false}
                    />
                  </div>

                  {/* Out Time */}
                  <div className="col-md-12 d-flex align-items-center gap-3 mt-3">
                    <label className="form-label w-25">Out Time</label>
                    <div className="input-icon-end position-relative flex-grow-1">
                      <DatePicker
                        className="form-control datetimepicker"
                        format="YYYY-MM-DD"
                        value={reguDate}
                        onChange={(date) => setReguDate(date)}
                        placeholder="YYYY-MM-DD"
                        disabled
                        // disabledDate={(current) => {
                        //   return current && current >= dayjs().startOf("day");
                        // }}
                      />
                      <span className="input-icon-addon">
                        <i className="ti ti-calendar text-gray-7" />
                      </span>
                    </div>
                    <TimePicker
                      className="form-control w-25"
                      value={outTime}
                      onChange={(value) => setOutTime(value)}
                      format="HH:mm:ss"
                      allowClear={false}
                    />
                  </div>
                </div>

                <div className="row mb-3">
                  <div className="col-md-6">
                    <label className="form-label">Reason</label>
                    <textarea
                      className="form-control"
                      rows={3}
                      placeholder="Enter Reason......"
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      required
                    ></textarea>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Total Hours</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="e.g. 08:00:00"
                      value={
                        inTime && outTime && outTime.isAfter(inTime)
                          ? (() => {
                              const diffSeconds = outTime.diff(
                                inTime,
                                "second",
                              );
                              const hrs = Math.floor(diffSeconds / 3600);
                              const mins = Math.floor(
                                (diffSeconds % 3600) / 60,
                              );
                              const secs = diffSeconds % 60;
                              return `${hrs.toString().padStart(2, "0")}:${mins
                                .toString()
                                .padStart(2, "0")}:${secs
                                .toString()
                                .padStart(2, "0")}`;
                            })()
                          : ""
                      }
                      readOnly
                    />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="submit" className="btn btn-secondary">
                  Submit
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default MyAttendance;
